import { useEffect, useState } from "react";

import { ActionMeta, MultiValue } from "react-select";

import FilterIcon from "@/assets/icons/adjustment-vertical.svg";
import PlusIcon from "@/assets/icons/plus.svg";
import CloseIcon from "@/assets/icons/xmark.svg";
import { AUDIO, NEWS, SOCIAL, Source } from "@/constants";
import newTheme from "@/new_theme";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import Button from "@components/Button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";
import { i18n } from "@lingui/core";

import AutoTagQuery from "./AutoTagQuery";
import CreatableSelectWithCopyPaste from "./CreatableSelectWithCopyPaste";
import FieldOptions from "./FieldOptions";
import {
  DropdownFilterControl,
  FilterControl,
  OpenDropdown,
} from "./FilterControl";
import {
  MinimumReachByChannelType,
  MinimumReachControls,
} from "./MinimumReachControls";
import { AddableUIQuery, UIQuery } from "./types";
import { getCustomStyles } from "./utils";

export type MentionType = "all" | "speech" | "text";

type QueryProps = {
  index: number;
  className?: string;
  removable?: boolean;

  keywords: string[];
  nearKeywords: string[];
  nearNearKeywords: string[];
  notNearKeywords: string[];

  languages: string[];
  languageOptions: string[];

  sources: Source[];
  sourceOptions: Source[];

  includeChannelNames: string[];
  excludeChannelNames: string[];
  excludeTopics: string[];

  mentionTypes?: MentionType;
  includePaywalled?: boolean;

  minReachByChannelType: MinimumReachByChannelType | { no_values: boolean };

  tags: string[];

  nearDistance?: number | string;
  notNearDistance?: number | string;

  addedControls?: Array<keyof UIQuery>;

  openDropdown?: OpenDropdown;
  setOpenDropdown: (v: OpenDropdown) => void;

  options: Partial<Record<keyof UIQuery, string[]>>;

  onRemove: () => void;
  onAddQueryCopySettings: () => void;

  onAddControl: (name: keyof Omit<UIQuery, "options">) => void;
  onRemoveControl: (name: keyof UIQuery) => void;

  onAddValue: (name: keyof AddableUIQuery, value: string) => string[];
  onSetValue: (field: keyof UIQuery, value: string) => void;
  onSetMinReachByChannelType: (value: MinimumReachByChannelType) => void;
  onRemoveValue: (name: keyof UIQuery, value: string) => string[];
  onCreateValue: (name: keyof AddableUIQuery, value: string) => void;
  canCreateValue: (value: string, name: keyof AddableUIQuery) => boolean;

  onKeywordChange: (
    values: string[],
    fieldName: keyof Omit<UIQuery, "options">
  ) => void;

  onRemoveKeyword: (value: string) => void;
  onRemoveNearKeyword: (value: string) => void;
  onRemoveNearNearKeyword: (value: string) => void;
  onRemoveNotNearKeyword: (value: string) => void;

  addQueryError: (error: string, index: number) => void;
  clearQueryError: (error: string, index: number) => void;
};

// Sync these with supported languages and sources
const LANGUAGES = {
  en: L.LANG_EN,
  sv: L.LANG_SV,
  no: L.LANG_NO,
  de: L.LANG_DE,
  da: L.LANG_DA,
  fi: L.LANG_FI,
  es: L.LANG_ES,
  fr: L.LANG_FR,
  it: L.LANG_IT,
  nl: L.LANG_NL,
  pt: L.LANG_PT,
};

const DISTANCE_OPTIONS: Array<number> = [
  1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 40, 50,
];

const MENTION_TYPES_FILTER_OPTIONS = {
  speech: L.MENTIONS_QUERYBUILDER_FILTER_MENTION_TYPE_SPEECH,
  text: L.MENTIONS_QUERYBUILDER_FILTER_MENTION_TYPE_TEXT,
};

const INCLUDE_PAYWALLED_FILTER_OPTIONS = {
  true: L.MENTIONS_QUERYBUILDER_FILTER_INCLUDE_PAYWALLED_LONG,
};

const SOURCES = {
  podcast: L.SOURCE_TYPE_PLURAL_PODCAST,
  radio: L.SOURCE_TYPE_RADIO,
  tv: L.SOURCE_TYPE_TV,
  youtube: L.SOURCE_TYPE_YOUTUBE,
  webtv: L.SOURCE_TYPE_WEB_TV,
  manual: L.SOURCE_TYPE_PLURAL_MANUAL,
  facebook: L.SOURCE_TYPE_FACEBOOK,
  instagram: L.SOURCE_TYPE_INSTAGRAM,
  tiktok: L.SOURCE_TYPE_TIKTOK,
  twitter: L.SOURCE_TYPE_TWITTER,
  linkedin: L.SOURCE_TYPE_LINKEDIN,
  news: L.SOURCE_TYPE_PLURAL_NEWS,
};

const SOURCE_GROUPS = [
  {
    keys: SOCIAL,
    label: L.MENTIONS_QUERYBUILDER_SOCIAL_LABEL,
  },
  {
    keys: AUDIO,
    label: L.MENTIONS_QUERYBUILDER_AUDIO_LABEL,
  },
  { keys: NEWS, label: L.MENTIONS_QUERYBUILDER_NEWS_LABEL },
];

type AllControlKeys = keyof Omit<
  UIQuery,
  | "uuid"
  | "keywords"
  | "nearNearKeywords"
  | "options"
  | "nearDistance"
  | "notNearDistance"
>;

const Query: React.FC<QueryProps> = (props) => {
  const [nearDistanceComponentOpen, setNearDistanceComponentOpen] =
    useState(false);
  const [notNearDistanceComponentOpen, setNotNearDistanceComponentOpen] =
    useState(false);

  const [showNearNearField, setShowNearNearField] = useState(false);

  const addError = (error: string) => props.addQueryError(error, props.index);
  const clearError = (error: string) =>
    props.clearQueryError(error, props.index);

  const allControls: Record<AllControlKeys, { short: string; long: string }> = {
    languages: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_LANGUAGES_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_LANGUAGES_LONG,
    },
    sources: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_SOURCES_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_SOURCES_LONG,
    },
    nearKeywords: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_NEAR_KEYWORDS_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_NEAR_KEYWORDS_LONG,
    },
    notNearKeywords: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_NOT_NEAR_KEYWORDS_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_NOT_NEAR_KEYWORDS_LONG,
    },
    includeChannelNames: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_INCLUDE_CHANNEL_NAMES_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_INCLUDE_CHANNEL_NAMES_LONG,
    },
    excludeChannelNames: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_EXCLUDE_CHANNEL_NAMES_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_EXCLUDE_CHANNEL_NAMES_LONG,
    },
    excludeTopics: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_EXCLUDE_TOPICS_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_EXCLUDE_TOPICS_LONG,
    },
    minReachByChannelType: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_EXCLUDE_MIN_REACH_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_EXCLUDE_MIN_REACH_LONG,
    },
    mentionTypes: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_MENTION_TYPES_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_MENTION_TYPES_LONG,
    },
    includePaywalled: {
      short: L.MENTIONS_QUERYBUILDER_FILTER_INCLUDE_PAYWALLED_SHORT,
      long: L.MENTIONS_QUERYBUILDER_FILTER_INCLUDE_PAYWALLED_LONG,
    },
    tags: {
      short: L.MENTIONS_QUERYBUILDER_AUTO_TAG_SHORT,
      long: L.MENTIONS_QUERYBUILDER_AUT_TAG_LONG,
    },
  };

  const getControlWithName = (name: keyof UIQuery) => {
    const sharedProps = {
      filterName: name,
      header: allControls[name as AllControlKeys] || "",
      onRemoveControl: () => props.onRemoveControl(name),
      onAddValue: (val: string) =>
        props.onAddValue(name as keyof AddableUIQuery, val),
      onSetValue: (field: keyof UIQuery, val: string) =>
        props.onSetValue(field, val),
      onRemoveValue: (val: string) => props.onRemoveValue(name, val),
      onCreateValue: (val: string) =>
        props.onCreateValue(name as keyof AddableUIQuery, val),

      index: props.index,
      openDropdown: props.openDropdown,
      setOpenDropdown: props.setOpenDropdown,
      testId: `${name}-filter`,
      addError,
      clearError,
    };

    return name === "languages" ? (
      <FilterControl
        key={name}
        icon={FilterIcon}
        canCreateNewOptions={false}
        options={props.languageOptions}
        values={props.languages}
        labels={Object.fromEntries(
          Object.entries(LANGUAGES).map(([k, v]) => [k, i18n._(v)])
        )}
        required={true}
        {...sharedProps}
      />
    ) : name === "sources" ? (
      <FilterControl
        key={name}
        icon={FilterIcon}
        canCreateNewOptions={false}
        options={props.sourceOptions}
        values={props.sources}
        groups={SOURCE_GROUPS.map(({ label, keys }) => ({
          label: i18n._(label),
          keys,
        }))}
        labels={Object.fromEntries(
          Object.entries(SOURCES).map(([k, v]) => [k, i18n._(v)])
        )}
        required={true}
        {...sharedProps}
      />
    ) : name === "includeChannelNames" ? (
      <FilterControl
        key={name}
        icon={FilterIcon}
        canCreateNewOptions={true}
        options={props.options.includeChannelNames || []}
        values={props.includeChannelNames}
        placeholder={L.MENTIONS_QUERYBUILDER_FILTER_SOURCES_PLACEHOLDER}
        {...sharedProps}
      />
    ) : name === "excludeChannelNames" ? (
      <FilterControl
        key={name}
        icon={FilterIcon}
        canCreateNewOptions={true}
        options={props.options.excludeChannelNames || []}
        values={props.excludeChannelNames}
        placeholder={L.MENTIONS_QUERYBUILDER_FILTER_SOURCES_PLACEHOLDER}
        {...sharedProps}
      />
    ) : name === "excludeTopics" ? (
      <FilterControl
        key={name}
        icon={FilterIcon}
        canCreateNewOptions={true}
        options={props.options.excludeTopics || []}
        values={props.excludeTopics}
        placeholder={L.MENTIONS_QUERYBUILDER_FILTER_TOPICS_PLACEHOLDER}
        {...sharedProps}
      />
    ) : name === "mentionTypes" && props.mentionTypes !== "all" ? (
      <DropdownFilterControl
        key={name}
        icon={FilterIcon}
        options={Object.fromEntries(
          Object.entries(MENTION_TYPES_FILTER_OPTIONS).map(([k, v]) => [
            k,
            i18n._(v),
          ])
        )}
        value={props.mentionTypes}
        {...sharedProps}
      />
    ) : name === "includePaywalled" ? (
      <DropdownFilterControl
        key={name}
        singleOption={
          Object.keys(INCLUDE_PAYWALLED_FILTER_OPTIONS).length === 1
        }
        icon={FilterIcon}
        options={Object.fromEntries(
          Object.entries(INCLUDE_PAYWALLED_FILTER_OPTIONS).map(([k, v]) => [
            `${k}`,
            i18n._(v),
          ])
        )}
        value={`${props.includePaywalled}`}
        {...sharedProps}
      />
    ) : name === "minReachByChannelType" ? (
      <MinimumReachControls
        key={name}
        minReachByChannelType={
          props.minReachByChannelType as MinimumReachByChannelType
        }
        sourceOptions={props.sourceOptions}
        onChange={props.onSetMinReachByChannelType}
        {...sharedProps}
      />
    ) : name === "tags" ? (
      <AutoTagQuery
        key={name}
        values={props.tags}
        icon={FilterIcon}
        placeholder={L.MENTIONS_QUERYBUILDER_AUTO_TAG_PLACEHOLDER}
        {...sharedProps}
      />
    ) : null;
  };

  const getInputId = (name: string): string => `${name}-input-${props.index}`;

  // Helpers to focus input element after pressing enter
  const sleep = (milliseconds: number) => {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  };

  const focus = (name: string) => {
    const id = getInputId(name);
    sleep(1).then(() => {
      const elm = document.getElementById(id);
      elm && elm.focus();
    });
  };

  const onCreateOption = (val: number | string, name: keyof AddableUIQuery) => {
    const splitString = val.toString().split(/[,]/);
    // this is only for when editing in place, should only affect a single keyword at a time
    if (splitString.length === 1) {
      return props.onCreateValue(name, splitString[0]);
    }
    splitString.forEach((val) => props.onCreateValue(name, val));
  };

  // PASTE
  const onPaste = (evt: React.ClipboardEvent, name: keyof AddableUIQuery) => {
    const clipboard = evt.clipboardData?.getData("Text");
    if (!clipboard) return;
    onCreateOption(clipboard, name);
    focus(name);
  };

  const onKeywordsPaste = (evt: React.ClipboardEvent) =>
    onPaste(evt, "keywords");
  const onNearKeywordsPaste = (evt: React.ClipboardEvent) =>
    onPaste(evt, "nearKeywords");
  const onNearNearKeywordsPaste = (evt: React.ClipboardEvent) =>
    onPaste(evt, "nearNearKeywords");
  const onNotNearKeywordsPaste = (evt: React.ClipboardEvent) =>
    onPaste(evt, "notNearKeywords");

  // CHANGE
  const onKeywordChange = (
    values: MultiValue<Option>,
    _actionMeta: ActionMeta<Option>
  ) =>
    props.onKeywordChange(
      values?.map(({ value }) => value),
      "keywords"
    );

  type Option = { value: string; label: string };

  const onNearKeywordChange = (
    newValue: MultiValue<Option>,
    _meta: ActionMeta<Option>
  ) => {
    const values = newValue?.map(({ value }) => ({ value })) ?? [];

    props.onKeywordChange(
      values.map((v) => v.value),
      "nearKeywords"
    );
  };

  const onNearNearKeywordChange = (
    newValue: MultiValue<Option>,
    _meta: ActionMeta<Option>
  ) => {
    const values = newValue?.map(({ value }) => ({ value })) ?? [];

    props.onKeywordChange(
      values.map((v) => v.value),
      "nearNearKeywords"
    );
  };

  const onNotNearKeywordChange = (
    newValue: MultiValue<Option>,
    _meta: ActionMeta<Option>
  ) => {
    const values = newValue?.map(({ value }) => ({ value })) ?? [];

    props.onKeywordChange(
      values.map((v) => v.value),
      "notNearKeywords"
    );
  };

  // CREATE
  const canCreateKeywords = (e: string) => props.canCreateValue(e, "keywords");
  const canCreateNearKeywords = (e: string) =>
    props.canCreateValue(e, "nearKeywords");
  const canCreateNearNearKeywords = (e: string) =>
    props.canCreateValue(e, "nearNearKeywords");
  const canCreateNotNearKeywords = (e: string) =>
    props.canCreateValue(e, "notNearKeywords");

  const onCreateKeywords = (e: string) => onCreateOption(e, "keywords");
  const onCreateNearKeywords = (e: string) => onCreateOption(e, "nearKeywords");
  const onCreateNearNearKeywords = (e: string) =>
    onCreateOption(e, "nearNearKeywords");
  const onCreateNotNearKeywords = (e: string) =>
    onCreateOption(e, "notNearKeywords");

  const onToggleNearDistanceComponent = (nextShow: boolean) => {
    setNearDistanceComponentOpen(nextShow);
  };

  const onToggleNotNearDistanceComponent = (nextShow: boolean) => {
    setNotNearDistanceComponentOpen(nextShow);
  };

  const onSetNearDistance = (e: React.ChangeEvent<HTMLInputElement>) =>
    props.onSetValue("nearDistance", e.target.value);

  const onSetNotNearDistance = (e: React.ChangeEvent<HTMLInputElement>) =>
    props.onSetValue("notNearDistance", e.target.value);

  const addNearNearField = () => {
    setShowNearNearField(true);
    setNearDistanceComponentOpen(false);
  };

  const showMinReachByChannelType = !Object.prototype.hasOwnProperty.call(
    props.minReachByChannelType,
    "no_values"
  );

  // The includePaywalled option is only relevant for organizations with news.
  const showIncludePaywalled = props.sourceOptions.includes("news");

  useEffect(() => {
    if (props.nearNearKeywords?.length > 0) setShowNearNearField(true);
  }, [props.nearNearKeywords]);

  return (
    <div
      className="p-5 relative bg-[var(--card-background)] border border-[var(--card-border-color)] mt-6"
      key={props.index}
      data-testid="query-row"
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between w-full">
          <AccessControl level={ACCESS_LEVELS.STAFF}>
            <Button
              className="mb-1 mr-4"
              variant="secondary"
              onClick={props.onAddQueryCopySettings}
            >
              {L.MENTIONS_QUERYBUILDER_COPY_SETTINGS}
            </Button>
          </AccessControl>
          {props.removable && (
            <Button
              variant="secondary"
              onClick={props.onRemove}
              icon={CloseIcon}
            />
          )}
        </div>

        {/* --- KEYWORDS --- */}
        <div className="keywords flex flex-col gap-1">
          <label>{L.MENTIONS_QUERYBUILDER_FILTER_KEYWORDS_LABEL}</label>
          <CreatableSelectWithCopyPaste
            focus={focus}
            styles={getCustomStyles()}
            toggledBackgroundColor={`${newTheme["gray-500"]}`}
            isMulti
            name="keywords"
            onKeywordsPaste={onKeywordsPaste}
            inputId={getInputId("keywords")}
            components={{
              Menu: () => null,
            }}
            onChange={onKeywordChange}
            canCreateOption={canCreateKeywords}
            onCreateOption={onCreateKeywords}
            onRemoveKeyword={props.onRemoveKeyword}
            value={props.keywords.map((v) => ({ label: v, value: v }))}
            placeholder={L.MENTIONS_QUERYBUILDER_FILTER_KEYWORDS_PLACEHOLDER}
            isClearable={false}
          />{" "}
          <small className="text-muted-foreground">
            {L.MENTIONS_QUERYBUILDER_FILTER_KEYWORDS_HELP}
          </small>
        </div>

        <div className="grid grid-cols-2 gap-5">
          {/* --- NEAR KEYWORDS --- */}
          <div className="flex-1 flex flex-col gap-1">
            <label>{L.MENTIONS_QUERYBUILDER_MENTIONED_TOGETHER_WITH}</label>

            <div className="flex gap-1">
              <div className="flex-1">
                <CreatableSelectWithCopyPaste
                  focus={focus}
                  styles={getCustomStyles("nearKeywords")}
                  toggledBackgroundColor={`${newTheme["green-300"]}`}
                  isMulti
                  name="nearKeywords"
                  onKeywordsPaste={onNearKeywordsPaste}
                  inputId={getInputId("nearKeywords")}
                  components={{
                    Menu: () => null,
                  }}
                  onChange={onNearKeywordChange}
                  canCreateOption={canCreateNearKeywords}
                  onCreateOption={onCreateNearKeywords}
                  onRemoveKeyword={props.onRemoveNearKeyword}
                  value={props.nearKeywords.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                  placeholder={
                    L.MENTIONS_QUERYBUILDER_FILTER_NEAR_KEYWORDS_LONG
                  }
                  isClearable={false}
                />
              </div>

              <FieldOptions
                value={props.nearDistance}
                onChange={onSetNearDistance}
                options={DISTANCE_OPTIONS}
                show={nearDistanceComponentOpen}
                onToggle={onToggleNearDistanceComponent}
                additionalFormElement={
                  !showNearNearField && (
                    <Button onClick={addNearNearField}>
                      {L.MENTIONS_QUERYBUILDER_FILTER_ADD_NEAR_NEAR_FIELD}
                    </Button>
                  )
                }
              />
            </div>

            <small className="text-muted-foreground">
              {L.MENTIONS_QUERYBUILDER_FILTER_NEAR_KEYWORDS_HELP_TEXT}
            </small>
          </div>

          {/* ---- NEAR NEAR KEYWORDS ---*/}
          {showNearNearField && (
            <div className="flex items-center flex-1 gap-5">
              <label>{L.MENTIONS_QUERYBUILDER_FILTER_NEAR_NEAR_FIELD}</label>

              <div className="flex-1">
                <CreatableSelectWithCopyPaste
                  focus={focus}
                  styles={getCustomStyles("nearNearKeywords")}
                  toggledBackgroundColor={`${newTheme["green-300"]}`}
                  isMulti
                  name="nearNearKeywords"
                  onKeywordsPaste={onNearNearKeywordsPaste}
                  inputId={getInputId("nearNearKeywords")}
                  components={{
                    Menu: () => null,
                  }}
                  onChange={onNearNearKeywordChange}
                  canCreateOption={canCreateNearNearKeywords}
                  onCreateOption={onCreateNearNearKeywords}
                  onRemoveKeyword={props.onRemoveNearNearKeyword}
                  value={props.nearNearKeywords.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                  placeholder={
                    L.MENTIONS_QUERYBUILDER_FILTER_NEAR_NEAR_KEYWORDS_PLACEHOLDER
                  }
                  isClearable={false}
                  isDisabled={props.nearKeywords.length === 0}
                />
              </div>
            </div>
          )}

          {/* --- NOT NEAR KEYWORDS ---*/}
          <div className="flex-1 flex flex-col gap-1">
            <label>{L.MENTIONS_QUERYBUILDER_NOT_MENTIONED_TOGETHER_WITH}</label>

            <div className="flex gap-1">
              <div className="flex-1">
                <CreatableSelectWithCopyPaste
                  focus={focus}
                  styles={getCustomStyles("notNearKeywords")}
                  toggledBackgroundColor={`${newTheme["red-300"]}`}
                  isMulti
                  name="notNearKeywords"
                  onKeywordsPaste={onNotNearKeywordsPaste}
                  inputId={getInputId("notNearKeywords")}
                  components={{
                    Menu: () => null,
                  }}
                  onChange={onNotNearKeywordChange}
                  canCreateOption={canCreateNotNearKeywords}
                  onCreateOption={onCreateNotNearKeywords}
                  onRemoveKeyword={props.onRemoveNotNearKeyword}
                  value={props.notNearKeywords.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                  placeholder={
                    L.MENTIONS_QUERYBUILDER_FILTER_NOT_NEAR_KEYWORDS_LONG
                  }
                  isClearable={false}
                />
              </div>
              <FieldOptions
                value={props.notNearDistance}
                onChange={onSetNotNearDistance}
                options={DISTANCE_OPTIONS}
                show={notNearDistanceComponentOpen}
                onToggle={onToggleNotNearDistanceComponent}
              />
            </div>
            <small className="text-muted-foreground">
              {L.MENTIONS_QUERYBUILDER_FILTER_NOT_NEAR_KEYWORDS_HELP_TEXT}
            </small>
          </div>
        </div>
      </div>

      {/* --- OTHER FILTERS --- */}
      <div className="flex flex-row flex-wrap gap-2.5 mt-5">
        {props.addedControls &&
          props.addedControls.map((name) => getControlWithName(name))}

        {(!props.addedControls ||
          Object.keys(allControls).length > props.addedControls.length) && (
          <div>
            <Dropdown>
              <DropdownToggle asChild>
                <Button variant="secondary" data-testid="other-filters-button">
                  <PlusIcon width={20} />
                </Button>
              </DropdownToggle>
              <DropdownMenu align="start">
                {Object.entries(allControls).map(
                  ([name, label]) =>
                    !(props.addedControls || []).includes(
                      name as keyof AddableUIQuery
                    ) &&
                    (name != "minReachByChannelType" ||
                      showMinReachByChannelType) &&
                    (name != "includePaywalled" || showIncludePaywalled) && (
                      <DropdownItem
                        key={name}
                        onClick={() => {
                          props.onAddControl(name as keyof AddableUIQuery);
                        }}
                      >
                        {label.long}
                      </DropdownItem>
                    )
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default Query;
